import React, { useState, useEffect, useRef } from "react";
import useApiHook from "../../../../../utils/useApi";
import { Filter, ChevronDown, Loader2 } from "lucide-react";
import {
  FilterRolePaginationData,
  Role,
} from "../../../../../interface/interfaces";

const FilterRoles: React.FC<{
  setSelectedRoleRef: (roleRef: string | null) => void;
}> = ({ setSelectedRoleRef }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, makeRequest } = useApiHook<{
    data: FilterRolePaginationData;
    statusCode: string;
    status: string;
  }>("/admin/roles", {
    method: "get",
  });

  // Initial data load
  useEffect(() => {
    makeRequest({ page: 1, size: 10 });
  }, []);

  // Handle data from API
  useEffect(() => {
    if (data?.data?.data) {
      if (page === 1) {
        setRoles(data.data.data);
      } else {
        setRoles((prevRoles) => [...prevRoles, ...data.data.data]);
      }

      // Check if there are more items to load
      setHasMore(data.data.data.length === 10);
      setIsLoadingMore(false);
    }
  }, [data]);

  // Load more data when scrolling to bottom
  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container || isLoadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop <= clientHeight * 1.5) {
      setIsLoadingMore(true);
      const nextPage = page + 1;
      setPage(nextPage);
      makeRequest({ page: nextPage, size: 10 });
    }
  };

  const handleRoleSelect = (roleReference: string, roleName: string) => {
    console.log("Selected role reference:", roleReference);
    setSelectedRole(roleName);
    setSelectedRoleRef(roleReference);
    setIsDropdownOpen(false);
  };

  const handleShowAll = () => {
    setSelectedRole(null);
    setSelectedRoleRef(null);
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".dropdown-container")) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <div className="relative dropdown-container">
      {/* Filter Button */}
      <div
        className="flex items-center py-2 px-3 rounded-md border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      >
        <Filter className="h-4 w-4 mr-2 text-gray-500" />
        <span className="text-gray-700 text-sm font-medium">
          {selectedRole ? selectedRole : "Filter"}
        </span>
        <ChevronDown className="h-4 w-4 ml-2 text-gray-500" />
      </div>

      {/* Dropdown */}
      {isDropdownOpen && (
        <div className="absolute left-0 top-full mt-1 w-64 bg-white rounded-md shadow-lg z-10 border border-gray-200">
          <div className="p-2">
            <p className="text-xs font-medium text-gray-500 mb-2 px-2">Role</p>

            {/* Show All Option */}
            <div
              className={`px-3 py-2 rounded-md cursor-pointer transition-colors ${
                selectedRole === null
                  ? "bg-blue-50 text-blue-700"
                  : "hover:bg-gray-50"
              }`}
              onClick={handleShowAll}
            >
              <span className="text-sm">Show All</span>
            </div>

            {/* Role Options */}
            {isLoading && page === 1 ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                <span className="text-sm text-gray-500">Loading...</span>
              </div>
            ) : roles.length > 0 ? (
              <div
                className="max-h-48 overflow-y-auto"
                ref={scrollContainerRef}
                onScroll={handleScroll}
              >
                {roles.map((role: Role) => (
                  <div
                    key={role.roleReference}
                    className={`px-3 py-2 rounded-md cursor-pointer transition-colors text-xs ${
                      selectedRole === role.roleName
                        ? "bg-blue-50 text-blue-700"
                        : "hover:bg-gray-50"
                    }`}
                    onClick={() =>
                      handleRoleSelect(role.roleReference, role.roleName)
                    }
                  >
                    <span className="text-sm">{role.roleName}</span>
                  </div>
                ))}

                {/* Loading indicator at bottom while fetching more */}
                {isLoadingMore && (
                  <div className="flex items-center justify-center py-2">
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    <span className="text-xs text-gray-500">
                      Loading more...
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-3 text-center text-sm text-gray-500">
                No roles found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterRoles;
